'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { TENANT_PLANS } from '@/lib/tenant-types';
import type { LabRegistrationForm } from '@/lib/tenant-types';
import { Check, ChevronRight, ChevronLeft, Building2, User, Phone, Mail, MapPin, CreditCard, Loader2, CheckCircle2, Shield, Zap, Star,  } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type Step = 'plan' | 'lab' | 'admin' | 'payment' | 'success';

const STEPS: { id: Step; label: string }[] = [
  { id: 'plan', label: 'Piano' },
  { id: 'lab', label: 'Laboratorio' },
  { id: 'admin', label: 'Account' },
  { id: 'payment', label: 'Pagamento' },
];

export default function RegisterPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>('plan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);

  const [form, setForm] = useState<LabRegistrationForm>({
    labName: '',
    vatNumber: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    plan: 'pro',
  });

  const updateForm = (field: keyof LabRegistrationForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const stepIndex = STEPS.findIndex(s => s.id === step);

  const handleNext = () => {
    if (step === 'plan') setStep('lab');
    else if (step === 'lab') {
      if (!form.labName.trim()) { setError('Il nome del laboratorio è obbligatorio.'); return; }
      if (!form.email.trim()) { setError('L\'email del laboratorio è obbligatoria.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError('Inserisci un indirizzo email valido.'); return; }
      setStep('admin');
    } else if (step === 'admin') {
      if (!form.adminName.trim()) { setError('Il nome completo è obbligatorio.'); return; }
      if (!form.adminEmail.trim()) { setError('L\'email amministratore è obbligatoria.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail.trim())) { setError('Inserisci un indirizzo email amministratore valido.'); return; }
      if (!form.adminPassword) { setError('La password è obbligatoria.'); return; }
      if (form.adminPassword.length < 8) { setError('La password deve essere di almeno 8 caratteri.'); return; }
      setStep('payment');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSaveError(null);
    try {
      const res = await fetch('/api/superadmin/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Errore durante la registrazione.');
      setTenantId(data.tenantId);
      setStep('success');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Errore sconosciuto.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = TENANT_PLANS.find(p => p.id === form.plan)!;

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">Registrazione completata!</h1>
          <p className="text-muted-foreground mb-2">
            Il tuo laboratorio <strong>{form.labName}</strong> è in fase di configurazione.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Riceverai un'email a <strong>{form.adminEmail}</strong> con le credenziali di accesso entro pochi minuti.
          </p>
          <div className="bg-muted rounded-xl p-4 mb-8 text-left">
            <p className="text-xs font-medium text-muted-foreground mb-2">COSA STIAMO CONFIGURANDO</p>
            <ul className="space-y-2">
              {['Database isolato per il tuo lab', 'Numero Twilio dedicato', 'Workspace Cal.com', 'Account amministratore', 'Email di benvenuto'].map(item => (
                <li key={item} className="flex items-center gap-2 text-sm text-foreground">
                  <Check className="w-4 h-4 text-green-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <Link href="/login" className="block w-full bg-primary text-white rounded-xl py-3 font-semibold hover:bg-primary/90 transition-colors text-center">
            Vai al Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-border px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-semibold text-lg text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Hai già un account? <span className="text-primary font-medium">Accedi</span>
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Step indicator */}
        <div className="flex items-center justify-center mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  i < stepIndex ? 'bg-green-500 text-white' :
                  i === stepIndex ? 'bg-primary text-white': 'bg-muted text-muted-foreground'
                }`}>
                  {i < stepIndex ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${i === stepIndex ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-px w-8 sm:w-16 mx-2 transition-colors ${i < stepIndex ? 'bg-green-500' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step: Plan Selection */}
        {step === 'plan' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">Scegli il tuo piano</h1>
              <p className="text-muted-foreground">Inizia con 14 giorni di prova gratuita. Nessuna carta richiesta.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {TENANT_PLANS.map(plan => (
                <button
                  key={plan.id}
                  onClick={() => updateForm('plan', plan.id)}
                  className={`relative rounded-2xl border-2 p-6 text-left transition-all hover:shadow-lg ${
                    form.plan === plan.id
                      ? 'border-primary bg-primary/5 shadow-lg'
                      : 'border-border bg-white hover:border-primary/40'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> PIÙ POPOLARE
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
                    {form.plan === plan.id && (
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-foreground">€{plan.price}</span>
                    <span className="text-muted-foreground text-sm">/mese</span>
                  </div>
                  <ul className="space-y-2">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-2 text-sm text-foreground">
                        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
            <div className="flex justify-center">
              <button onClick={handleNext} className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                Continua con {selectedPlan.name} <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step: Lab Details */}
        {step === 'lab' && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Dati del Laboratorio</h1>
              <p className="text-muted-foreground">Inserisci le informazioni del tuo laboratorio.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Nome Laboratorio *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text" value={form.labName} onChange={e => updateForm('labName', e.target.value)}
                    placeholder="es. Laboratorio Rossi"
                    aria-required="true"
                    aria-invalid={error && !form.labName.trim() ? 'true' : undefined}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Laboratorio *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email" value={form.email} onChange={e => updateForm('email', e.target.value)}
                    placeholder="info@laboratorio.it"
                    aria-required="true"
                    aria-invalid={error && !form.email.trim() ? 'true' : undefined}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Telefono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="tel" value={form.phone} onChange={e => updateForm('phone', e.target.value)}
                      placeholder="+39 02 1234567"
                      className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">P.IVA</label>
                  <input
                    type="text" value={form.vatNumber} onChange={e => updateForm('vatNumber', e.target.value)}
                    placeholder="IT12345678901"
                    className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Indirizzo</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text" value={form.address} onChange={e => updateForm('address', e.target.value)}
                      placeholder="Via Roma 1"
                      className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Città</label>
                  <input
                    type="text" value={form.city} onChange={e => updateForm('city', e.target.value)}
                    placeholder="Milano"
                    className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              {error && (
                <div role="alert" className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {error}
                </div>
              )}
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => setStep('plan')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="w-4 h-4" /> Indietro
              </button>
              <button onClick={handleNext} className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                Continua <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step: Admin Account */}
        {step === 'admin' && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Account Amministratore</h1>
              <p className="text-muted-foreground">Crea le credenziali per l'amministratore del laboratorio.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Nome Completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text" value={form.adminName} onChange={e => updateForm('adminName', e.target.value)}
                    placeholder="Mario Rossi"
                    aria-required="true"
                    aria-invalid={error && !form.adminName.trim() ? 'true' : undefined}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Amministratore *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email" value={form.adminEmail} onChange={e => updateForm('adminEmail', e.target.value)}
                    placeholder="admin@laboratorio.it"
                    aria-required="true"
                    aria-invalid={error && !form.adminEmail.trim() ? 'true' : undefined}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password *</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password" value={form.adminPassword} onChange={e => updateForm('adminPassword', e.target.value)}
                    placeholder="Minimo 8 caratteri"
                    aria-required="true"
                    aria-invalid={error && (!form.adminPassword || form.adminPassword.length < 8) ? 'true' : undefined}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  <Shield className="w-3.5 h-3.5 inline mr-1" />
                  Piano selezionato: <strong>{selectedPlan.name}</strong> — €{selectedPlan.price}/mese
                </p>
              </div>
              {error && (
                <div role="alert" className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {error}
                </div>
              )}
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => setStep('lab')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="w-4 h-4" /> Indietro
              </button>
              <button onClick={handleNext} className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                Continua <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step: Payment */}
        {step === 'payment' && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2">Riepilogo e Pagamento</h1>
              <p className="text-muted-foreground">Inizia con 14 giorni di prova gratuita.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 space-y-6">
              {/* Order summary */}
              <div className="bg-muted rounded-xl p-5">
                <h3 className="font-semibold text-foreground mb-3">Riepilogo Ordine</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Laboratorio</span>
                    <span className="font-medium text-foreground">{form.labName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Piano</span>
                    <span className="font-medium text-foreground">{selectedPlan.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prova gratuita</span>
                    <span className="font-medium text-green-600">14 giorni</span>
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between">
                    <span className="font-semibold text-foreground">Totale oggi</span>
                    <span className="font-bold text-green-600">€0,00</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Poi dal giorno 15</span>
                    <span>€{selectedPlan.price}/mese</span>
                  </div>
                </div>
              </div>

              {/* Payment method placeholder */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  <CreditCard className="w-4 h-4 inline mr-1.5" />
                  Metodo di Pagamento
                </label>
                <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                  <CreditCard className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Stripe Checkout sarà disponibile al lancio.</p>
                  <p className="text-xs text-muted-foreground mt-1">Per ora la registrazione è gratuita durante il beta.</p>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Shield className="w-4 h-4 shrink-0 mt-0.5 text-green-500" />
                <span>I tuoi dati sono protetti con crittografia SSL. Puoi cancellare in qualsiasi momento.</span>
              </div>

              {error && <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>}
              {saveError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  <p className="text-sm text-red-700">{saveError}</p>
                </div>
              )}
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => setStep('admin')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="w-4 h-4" /> Indietro
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Configurazione...</> : <><Zap className="w-4 h-4" /> Avvia il tuo Lab</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
